import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, User, LogOut, Shield, ChevronDown, ArrowRight, ArrowLeft, Users } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useAdminAuth } from '../../contexts/AdminAuthContext'

interface NavItem {
  name: string
  href?: string
  children?: { name: string; href: string }[]
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const { user, signOut } = useAuth()
  const { isAdmin, adminSignOut } = useAdminAuth()
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const navigation: NavItem[] = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    {
      name: 'Coaching',
      children: [
        { name: 'Coaching & Investment', href: '/coaching' },
        { name: 'The Strong Again Method™', href: '/private-training' },
        { name: 'Nutrition Blueprint', href: '/meal-plan' },
        { name: 'Physique & Competition Coaching', href: '/online-training' },
      ],
    },
    {
      name: 'Resources',
      children: [
        {
          name: 'Which Starting-Over Story Is Yours?',
          href: '/starting-over-guide',
        },
        { name: 'The First Step: A Free Reading', href: '/coming-home-to-me' },
        { name: 'Macro Calculator', href: '/macro-calculator' },
      ],
    },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (path: string) => location.pathname === path  


  const isDropdownActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => isActive(child.href))
    }
    return false
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleSignOut = async () => {
    try {
      if (isAdmin) {
        await adminSignOut()
      } else {
        await signOut()
      }
      setIsUserMenuOpen(false)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

const rawAccountName = user?.user_metadata?.full_name?.trim() || ''

const accountDisplayName =
  rawAccountName.toLowerCase().includes('stutsman')
    ? 'Cassie Tach'
    : rawAccountName || (isAdmin ? 'Admin' : 'Account')

  // Admin viewing /admin/client-view (a read-only layout preview of the
  // client dashboard, from their own admin account — never a real client's
  // account) gets a "Back to Admin Dashboard" treatment on the main nav
  // button instead of the usual "Admin Dashboard" label, since they're
  // already past that point.
  const onClientPreview = isAdmin && location.pathname === '/admin/client-view'

  return (
    <header className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-brand-teal text-brand-ivory text-center text-xs sm:text-sm py-2 px-4">
        <Link
          to="/book-consultation"
          className="inline-flex items-center gap-2 font-semibold tracking-wide hover:opacity-80 transition-opacity"
        >
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-brand-bronze animate-pulse" />
          Founding coaching spots now open — schedule your complimentary consultation
          <ArrowRight size={13} aria-hidden />
        </Link>
      </div>

      <div
        className={`transition-all duration-300 border-b ${
          isScrolled
            ? 'bg-brand-navy/95 backdrop-blur-xl shadow-soft-lg border-brand-cream/15'
            : 'bg-brand-navy border-brand-cream/10'
        }`}
      >
        <div className="container-max">
          <div className="flex justify-between items-center py-3.5 sm:py-4 px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <Link to="/" className="flex items-center group shrink-0">
              <img
                src="/images/logo-light.webp"
                alt="Fit by Cassie"
                className="h-10 sm:h-11 w-auto transition-opacity duration-300 group-hover:opacity-85"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-7" ref={dropdownRef}>
              {navigation.map((item) => (
                item.children ? (
                  <div key={item.name} className="relative">
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                      className={`relative text-sm font-medium tracking-wide transition-colors duration-200 hover:text-brand-ivory flex items-center gap-1 py-1 ${
                        isDropdownActive(item)
                          ? 'text-brand-ivory'
                          : 'text-brand-cream/80'
                      }`}
                    >
                      {item.name}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${
                          openDropdown === item.name ? 'rotate-180' : ''
                        }`}
                      />
                      {isDropdownActive(item) && (
                        <span className="absolute -bottom-1 left-0 right-4 h-0.5 rounded-full bg-brand-bronze" />
                      )}
                    </button>

                    <AnimatePresence>
                      {openDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 mt-3 w-64 bg-brand-ivory rounded-2xl shadow-soft-lg border border-brand-bronze/20 py-2 z-50 overflow-hidden"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`block px-5 py-2.5 text-sm transition-colors ${
                                isActive(child.href)
                                  ? 'text-brand-navy bg-brand-cream font-semibold'
                                  : 'text-brand-navy/80 hover:bg-brand-cream hover:text-brand-navy'
                              }`}
                              onClick={() => setOpenDropdown(null)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={`relative text-sm font-medium tracking-wide transition-colors duration-200 hover:text-brand-ivory py-1 ${
                      isActive(item.href!)
                        ? 'text-brand-ivory'
                        : 'text-brand-cream/80'
                    }`}
                  >
                    {item.name}
                    {isActive(item.href!) && (
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-brand-bronze" />
                    )}
                  </Link>
                )
              ))}
            </nav>

            {/* User Menu / Auth Buttons */}
            <div className="hidden lg:flex items-center gap-5">
              {user ? (
                <div className="relative flex items-center gap-3">
                  <Link
                    to={isAdmin ? '/admin/dashboard' : '/dashboard'}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ivory border border-brand-cream/40 rounded-full px-6 py-2.5 hover:bg-brand-cream hover:text-brand-navy hover:border-brand-cream transition-colors duration-300"
                  >
                    {onClientPreview ? <ArrowLeft size={16} /> : isAdmin ? <Shield size={16} /> : <User size={16} />}
                    {onClientPreview ? 'Back to Admin Dashboard' : isAdmin ? 'Admin Dashboard' : 'My Dashboard'}
                  </Link>

                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="p-2 text-brand-cream/80 hover:text-brand-ivory transition-colors"
                    aria-label="Account menu"
                  >
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute right-0 top-full mt-3 w-52 bg-brand-ivory rounded-2xl shadow-soft-lg border border-brand-bronze/20 overflow-hidden"
                      >
                        <div className="py-2">
                          <div className="px-5 py-2 text-xs font-semibold uppercase tracking-widest-xl text-brand-navy/50">
                            {accountDisplayName}
                          </div>

                          {isAdmin ? (
                            <Link
                              to="/admin/client-view"
                              className="flex items-center gap-2 px-5 py-2.5 text-sm text-brand-navy/80 hover:bg-brand-cream hover:text-brand-navy transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <User size={15} />
                              Preview Client Dashboard
                            </Link>
                          ) : (
                            <Link
                              to="/dashboard/community"
                              className="flex items-center gap-2 px-5 py-2.5 text-sm text-brand-navy/80 hover:bg-brand-cream hover:text-brand-navy transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <Users size={15} />
                              Community Center
                            </Link>
                          )}

                          <button
                            onClick={handleSignOut}
                            className="w-full text-left px-5 py-2.5 text-sm text-brand-navy/80 hover:bg-brand-cream hover:text-brand-navy transition-colors flex items-center gap-2"
                          >
                            <LogOut size={16} />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-semibold text-brand-ivory border border-brand-cream/40 rounded-full px-6 py-2.5 hover:bg-brand-cream hover:text-brand-navy hover:border-brand-cream transition-colors duration-300"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-brand-cream hover:text-brand-ivory transition-colors p-1"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden border-t border-brand-cream/10 bg-brand-navy"
              >
                <div className="px-5 py-5 space-y-4">
                  {navigation.map((item) => (
                    item.children ? (
                      <div key={item.name} className="space-y-2">
                        <div className="text-xs font-semibold text-brand-teal uppercase tracking-widest-xl">
                          {item.name}
                        </div>

                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`block text-base font-medium pl-3 transition-colors duration-200 ${
                              isActive(child.href)
                                ? 'text-brand-ivory'
                                : 'text-brand-cream/80 hover:text-brand-ivory'
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href!}
                        className={`block text-base font-medium transition-colors duration-200 ${
                          isActive(item.href!)
                            ? 'text-brand-ivory'
                            : 'text-brand-cream/80 hover:text-brand-ivory'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}

                  <div className="pt-4 border-t border-brand-cream/10">
                    {user ? (
                      <div className="space-y-4">
                        <Link
                          to={isAdmin ? '/admin/dashboard' : '/dashboard'}
                          className="inline-flex items-center gap-2 text-base font-semibold text-brand-ivory border border-brand-cream/40 rounded-full px-6 py-2.5 hover:bg-brand-cream hover:text-brand-navy transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {onClientPreview ? <ArrowLeft size={16} /> : isAdmin ? <Shield size={16} /> : <User size={16} />}
                          {onClientPreview ? 'Back to Admin Dashboard' : isAdmin ? 'Admin Dashboard' : 'My Dashboard'}
                        </Link>

                        {isAdmin ? (
                          <Link
                            to="/admin/client-view"
                            className="flex items-center gap-2 text-base font-medium text-brand-cream/80 hover:text-brand-ivory transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <User size={16} />
                            Preview Client Dashboard
                          </Link>
                        ) : (
                          <Link
                            to="/dashboard/community"
                            className="flex items-center gap-2 text-base font-medium text-brand-cream/80 hover:text-brand-ivory transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <Users size={16} />
                            Community Center
                          </Link>
                        )}

                        <button
                          onClick={() => {
                            handleSignOut()
                            setIsMenuOpen(false)
                          }}
                          className="block text-base font-medium text-brand-cream/80 hover:text-brand-ivory transition-colors"
                        >
                          Sign Out
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/login"
                        className="inline-flex items-center text-base font-semibold text-brand-ivory border border-brand-cream/40 rounded-full px-6 py-2.5 hover:bg-brand-cream hover:text-brand-navy transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Sign In
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  )
}

export default Header
